/**
 * Сводка по счетам для страницы «Статистика».
 *
 * Счёт выписывается подтверждением раскладки на объект или подобъект за день:
 * сколько человек прошло и сколько списано со склада. Страница только
 * складывает эти числа — ничего не досчитывает и не экстраполирует. День без
 * счёта значит «раскладку не подтверждали», а не «людей не было»; такие дни
 * видны в графике нулевой высотой и в счётчике покрытия.
 */

import type { Account } from '@/features/accounts/types';
import { MEALS, type MealId } from '@/features/menu/types';
import { parseIsoDate, toIsoDate } from '@/features/menu/dates';
import type { ObjectItem } from '@/features/objects/types';

/** Значение фильтра «вся организация». */
export const ALL_OBJECTS = 'all';

export interface StatisticsFilters {
  /** гггг-мм-дд, включительно. */
  from: string;
  /** гггг-мм-дд, включительно. */
  to: string;
  /** Объект верхнего уровня (с подобъектами) или null — вся организация. */
  objectId: string | null;
}

export interface DayPoint {
  date: string;
  /** Человек за день: сумма по счетам дня. */
  people: number;
  /** Начислено за день, ₽. */
  charged: number;
  /** Был ли за этот день хотя бы один счёт. */
  hasData: boolean;
}

export interface Totals {
  /** Сумма людей по дням (человеко-дни). */
  peopleDays: number;
  charged: number;
  daysWithData: number;
  /** Длина выбранного периода в днях. */
  days: number;
  avgPeoplePerDay: number;
  avgChargedPerDay: number;
  /** Средняя стоимость человека-дня, ₽. */
  costPerPerson: number;
}

export interface ObjectStat {
  objectId: string;
  name: string;
  peopleDays: number;
  charged: number;
  costPerPerson: number;
  /** Доля в начислениях периода, 0..1. */
  share: number;
}

export interface MonthStat {
  /** гггг-мм. */
  month: string;
  peopleDays: number;
  charged: number;
  /** Средний расход на день, по которому есть счёт. */
  avgChargedPerDay: number;
  costPerPerson: number;
  daysWithData: number;
}

export interface DietStat {
  id: string;
  name: string;
  /** Порции за период: сумма людей по приёмам пищи. */
  portions: number;
  charged: number;
  /** Доля в начислениях периода, 0..1. */
  share: number;
}

export interface MealStat {
  mealId: MealId;
  portions: number;
  charged: number;
  /** Доля в начислениях периода, 0..1. */
  share: number;
}

/**
 * Строка «расход против лимита». Лимит дня рациона — его цена на день,
 * умноженная на людей рациона за день; людей берём по максимуму среди приёмов
 * пищи, а не суммой, иначе человек с завтраком и обедом посчитался бы дважды.
 */
export interface BudgetRow {
  id: string;
  name: string;
  /** Человеко-дни на рационах с известной ценой. */
  peopleDays: number;
  /** Списано, ₽. */
  spent: number;
  /** Лимит, ₽; 0 — ни для одного рациона строки цена не известна. */
  limit: number;
}

export interface Budget extends BudgetRow {
  /**
   * Списано по рационам, цена которых неизвестна (рацион удалён или цена
   * не задана): в сравнение с лимитом не входит, показывается отдельно.
   */
  spentWithoutLimit: number;
  diets: BudgetRow[];
  objects: BudgetRow[];
}

export interface Statistics {
  totals: Totals;
  budget: Budget;
  /** Все дни периода подряд — для графика и графика-оси. */
  days: DayPoint[];
  objects: ObjectStat[];
  months: MonthStat[];
  diets: DietStat[];
  meals: MealStat[];
}

/**
 * Дни периода подряд, включительно. Больше тысячи дней не перебираем:
 * DatePicker теоретически даёт любую дату, а рисовать десятилетие на графике
 * всё равно бессмысленно.
 */
export function enumerateDays(from: string, to: string): string[] {
  if (!from || !to || from > to) return [];

  const days: string[] = [];
  const cursor = parseIsoDate(from);
  const end = parseIsoDate(to);
  while (cursor <= end && days.length < 1000) {
    days.push(toIsoDate(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }

  return days;
}

/** Подобъект входит в выбранный объект; цепочка родителей — не глубже десяти. */
function isUnder(
  objectId: string,
  selectedId: string | null,
  parentOf: Map<string, string>
): boolean {
  if (selectedId === null) return true;

  let current: string | undefined = objectId;
  for (let depth = 0; current && depth < 10; depth += 1) {
    if (current === selectedId) return true;
    current = parentOf.get(current);
  }

  return false;
}

function safeRatio(value: number, total: number): number {
  return total > 0 ? value / total : 0;
}

function addToBudgetRow(
  map: Map<string, BudgetRow>,
  id: string,
  name: string,
  value: { peopleDays: number; spent: number; limit: number }
): void {
  const row = map.get(id) ?? { id, name, peopleDays: 0, spent: 0, limit: 0 };
  row.peopleDays += value.peopleDays;
  row.spent += value.spent;
  row.limit += value.limit;
  map.set(id, row);
}

/**
 * Складывает счета в сводку; фильтры по периоду и объекту — здесь же.
 * `dietPrices` — цена дня рациона по id: из неё считается лимит.
 */
export function collectStatistics(
  accounts: Account[],
  objects: ObjectItem[],
  filters: StatisticsFilters,
  dietPrices: Map<string, number> = new Map()
): Statistics {
  const parentOf = new Map(objects.map((object) => [object.id, object.parentObjectId]));
  const nameOf = new Map(objects.map((object) => [object.id, object.name]));

  const scoped = accounts.filter(
    (account) =>
      account.date >= filters.from &&
      account.date <= filters.to &&
      isUnder(account.objectId, filters.objectId, parentOf)
  );

  const dayMap = new Map<string, { people: number; charged: number }>();
  const objectMap = new Map<string, { people: number; charged: number }>();
  const monthMap = new Map<string, { people: number; charged: number; dates: Set<string> }>();
  const dietMap = new Map<string, { name: string; portions: number; charged: number }>();
  const mealMap = new Map<MealId, { portions: number; charged: number }>();
  const accountNameOf = new Map<string, string>();
  const budgetDiets = new Map<string, BudgetRow>();
  const budgetObjects = new Map<string, BudgetRow>();
  let spentWithoutLimit = 0;

  for (const account of scoped) {
    const day = dayMap.get(account.date) ?? { people: 0, charged: 0 };
    day.people += account.passed;
    day.charged += account.charged;
    dayMap.set(account.date, day);

    const object = objectMap.get(account.objectId) ?? { people: 0, charged: 0 };
    object.people += account.passed;
    object.charged += account.charged;
    objectMap.set(account.objectId, object);
    if (!accountNameOf.has(account.objectId)) {
      accountNameOf.set(account.objectId, account.objectName);
    }

    const monthKey = account.date.slice(0, 7);
    const month = monthMap.get(monthKey) ?? { people: 0, charged: 0, dates: new Set<string>() };
    month.people += account.passed;
    month.charged += account.charged;
    month.dates.add(account.date);
    monthMap.set(monthKey, month);

    for (const meal of account.meals ?? []) {
      const mealStat = mealMap.get(meal.mealId) ?? { portions: 0, charged: 0 };
      mealStat.portions += meal.passed;
      mealStat.charged += meal.charged;
      mealMap.set(meal.mealId, mealStat);

      for (const diet of meal.diets ?? []) {
        const key = diet.dietId || diet.dietName;
        const dietStat = dietMap.get(key) ?? { name: diet.dietName, portions: 0, charged: 0 };
        dietStat.portions += diet.passed;
        dietStat.charged += diet.charged;
        dietMap.set(key, dietStat);
      }
    }

    // Расход против лимита: по рационам счёта за день.
    const dayDiets = new Map<string, { name: string; people: number; spent: number }>();
    for (const meal of account.meals ?? []) {
      for (const diet of meal.diets ?? []) {
        const key = diet.dietId || diet.dietName;
        const value = dayDiets.get(key) ?? { name: diet.dietName, people: 0, spent: 0 };
        value.people = Math.max(value.people, diet.passed);
        value.spent += diet.charged;
        dayDiets.set(key, value);
      }
    }
    for (const [dietId, value] of dayDiets) {
      const price = dietPrices.get(dietId);
      if (price === undefined || price <= 0) {
        spentWithoutLimit += value.spent;
        continue;
      }
      const row = { peopleDays: value.people, spent: value.spent, limit: price * value.people };
      addToBudgetRow(budgetDiets, dietId, value.name, row);
      addToBudgetRow(
        budgetObjects,
        account.objectId,
        accountNameOf.get(account.objectId) ?? account.objectName,
        row
      );
    }
  }

  const peopleDays = [...dayMap.values()].reduce((sum, day) => sum + day.people, 0);
  const charged = [...dayMap.values()].reduce((sum, day) => sum + day.charged, 0);
  const daysWithData = dayMap.size;

  const days: DayPoint[] = enumerateDays(filters.from, filters.to).map((date) => {
    const day = dayMap.get(date);

    return {
      date,
      people: day?.people ?? 0,
      charged: day?.charged ?? 0,
      hasData: day !== undefined,
    };
  });

  const objectsStats = [...objectMap.entries()]
    .map(([objectId, value]): ObjectStat => ({
      objectId,
      name: accountNameOf.get(objectId) ?? nameOf.get(objectId) ?? objectId,
      peopleDays: value.people,
      charged: value.charged,
      costPerPerson: safeRatio(value.charged, value.people),
      share: safeRatio(value.charged, charged),
    }))
    .sort((a, b) => b.charged - a.charged);

  const months = [...monthMap.entries()]
    .map(([month, value]): MonthStat => ({
      month,
      peopleDays: value.people,
      charged: value.charged,
      avgChargedPerDay: safeRatio(value.charged, value.dates.size),
      costPerPerson: safeRatio(value.charged, value.people),
      daysWithData: value.dates.size,
    }))
    .sort((a, b) => a.month.localeCompare(b.month));

  const diets = [...dietMap.entries()]
    .map(([id, value]): DietStat => ({
      id,
      name: value.name,
      portions: value.portions,
      charged: value.charged,
      share: safeRatio(value.charged, charged),
    }))
    .sort((a, b) => b.charged - a.charged);

  const meals = MEALS.flatMap((meal): MealStat[] => {
    const value = mealMap.get(meal.id);
    if (!value) return [];

    return [
      {
        mealId: meal.id,
        portions: value.portions,
        charged: value.charged,
        share: safeRatio(value.charged, charged),
      },
    ];
  });

  const budgetDietRows = [...budgetDiets.values()].sort((a, b) => b.spent - a.spent);
  const budgetObjectRows = [...budgetObjects.values()].sort((a, b) => b.spent - a.spent);

  return {
    budget: {
      id: ALL_OBJECTS,
      name: '',
      peopleDays: budgetDietRows.reduce((sum, row) => sum + row.peopleDays, 0),
      spent: budgetDietRows.reduce((sum, row) => sum + row.spent, 0),
      limit: budgetDietRows.reduce((sum, row) => sum + row.limit, 0),
      spentWithoutLimit,
      diets: budgetDietRows,
      objects: budgetObjectRows,
    },
    totals: {
      peopleDays,
      charged,
      daysWithData,
      days: days.length,
      avgPeoplePerDay: safeRatio(peopleDays, daysWithData),
      avgChargedPerDay: safeRatio(charged, daysWithData),
      costPerPerson: safeRatio(charged, peopleDays),
    },
    days,
    objects: objectsStats,
    months,
    diets,
    meals,
  };
}
